/**
 * useDebounce.ts
 *
 * Custom React hook for debouncing a value or function callback.
 */

// oxlint-disable-next-line endla/no-compiler-opt-out -- Existing code
"use no memo";

import { useEffect, useState } from "react";

/**
 * Debounces a value or a callback function.
 *
 * @param value - The value or callback to debounce.
 * @param delay - The debounce delay in milliseconds.
 * @returns The debounced value or a debounced callback.
 */
export function useDebounce<T>(value: T, delay: number): T {
  // taken from https://usehooks.com/useDebounce/
  // if a value if updated regularly this hook waits for it to not be updated for delay ms
  // once delay ms has elapsed the value will update to the latest value.
  // useful for preventing expensive actions being fired too often like fetching/posting data to the server

  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  // oxlint-disable-next-line regex/invalid -- A useEffect is pretty essential here for debouncing.
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}
